import type {
	Checkout,
	CheckoutStep,
	DeclarationAnswer,
	DeclarationRule,
} from '@/types/order-checkout'

export const orderSteps = [
	'driver',
	'plan',
	'delivery',
	'eligibility',
	'declaration',
	'summary',
	'payment',
] as const

export type OrderStep = (typeof orderSteps)[number]

export type CheckoutErrors = Record<string, string[]>

// The step of a /checkout/{uuid}/{step} path; null for /checkout/{uuid}
export const getCheckoutStepFromPath = (path: string): CheckoutStep | null =>
	(path.split('/').filter(Boolean)[2] as CheckoutStep | undefined) ?? null

const stepIndex = (step: CheckoutStep | null) =>
	(orderSteps as readonly string[]).indexOf(step ?? '')

/**
 * The step a checkout page has to redirect to, or null when the requested step is open.
 * Editable presales open every step up to the one they resume on, confirmed ones only payment
 * and paid ones only the thank-you page. Without a step (`/checkout/{uuid}`) it's the resume step.
 */
export const getCheckoutRedirectStep = (
	checkout: Pick<Checkout, 'status' | 'step'>,
	requestedStep: CheckoutStep | null
): CheckoutStep | null => {
	if (checkout.status === 'completed') {
		return requestedStep === 'thank-you' ? null : 'thank-you'
	}

	if (checkout.status === 'pending_payment') {
		return requestedStep === 'payment' ? null : 'payment'
	}

	const resumeIndex = Math.min(
		Math.max(stepIndex(checkout.step), 0),
		stepIndex('summary')
	)
	const requestedIndex = stepIndex(requestedStep)

	if (requestedIndex === -1 || requestedIndex > resumeIndex) {
		return orderSteps[resumeIndex]
	}

	return null
}

/**
 * Whether a confirmed presale can go back to this step, which reopens it first.
 */
export const canReopenAt = (
	checkout: Pick<Checkout, 'status' | 'can_return_to_summary'>,
	step: CheckoutStep
): boolean =>
	checkout.status === 'pending_payment' &&
	checkout.can_return_to_summary &&
	stepIndex(step) !== -1 &&
	stepIndex(step) <= stepIndex('summary')

/**
 * The API's error body as messages per field: 422s carry `data.messages`, other errors only a `message`.
 */
export const getCheckoutErrors = (error: unknown): CheckoutErrors => {
	const body = (
		error as {
			data?: {
				message?: string
				errors?: string[]
				data?: { messages?: CheckoutErrors }
			}
		}
	)?.data

	if (body?.data?.messages) {
		return body.data.messages
	}

	// The presale cancel endpoint answers with an `errors` list
	if (Array.isArray(body?.errors) && body.errors.length) {
		return { message: body.errors }
	}

	return {
		message: [body?.message || 'An error occurred. Please try again.'],
	}
}

// The first API error message, for errors shown next to a single field
export const getCheckoutErrorMessage = (error: unknown): string =>
	Object.values(getCheckoutErrors(error))[0][0]

/**
 * Y-m-d, as the API takes dates, from a Y-m-d[ H:i:s] or d/m/Y value.
 */
export const toIsoDate = (value?: string | null): string => {
	if (!value) return ''

	const iso = value.match(/^(\d{4}-\d{2}-\d{2})/)
	if (iso) return iso[1]

	const au = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
	return au ? `${au[3]}-${au[2]}-${au[1]}` : ''
}

/**
 * Whether an answer meets its declaration question's rule, compared the way the API compares it.
 * No rule accepts any answer; an unknown operator accepts none.
 */
export const meetsDeclarationRule = (
	answer: DeclarationAnswer | undefined,
	rule: DeclarationRule | null
): boolean => {
	if (!rule) return true

	// Numbers can arrive as text, e.g. "3"
	const value =
		typeof answer === 'string' && answer.trim() && !isNaN(Number(answer))
			? Number(answer)
			: answer
	const expected = rule.value

	switch (rule.type) {
		case 'is':
		case '===':
			return value === expected
		case '!==':
			return value !== expected
		case '==':
			return value == expected
		case '!=':
			return value != expected
		case '<':
			return (value as number) < (expected as number)
		case '<=':
			return (value as number) <= (expected as number)
		case '>':
			return (value as number) > (expected as number)
		case '>=':
			return (value as number) >= (expected as number)
		default:
			return false
	}
}

/**
 * Today as Y-m-d in Melbourne, where the API checks dates against today.
 */
export const apiToday = (): string =>
	new Intl.DateTimeFormat('en-CA', {
		timeZone: 'Australia/Melbourne',
	}).format(new Date())

const MEDICARE_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9]

/**
 * 10 digits starting 2-6, whose 9th digit is the weighted sum of the first 8, mod 10.
 */
export const isValidMedicareNumber = (value?: string | null): boolean => {
	const match = (value ?? '').match(/^([2-6]\d{7})(\d)\d$/)
	if (!match) return false

	const [, base = '', checkDigit] = match
	const sum = MEDICARE_WEIGHTS.reduce(
		(total, weight, index) => total + Number(base[index]) * weight,
		0
	)

	return sum % 10 === Number(checkDigit)
}
